import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Typography,
  makeStyles,
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import moment from "moment";
import CMOSelect from "./CMOSelect";
import SponsorSelect from "./SponsorSelect";
import BrandSelect from "./BrandSelect";
// import MFGPlantMultiSelect from "./MFGPlantMultiSelect";
import MFGPlantSelect from "./MFGPlantSelect";
import MonthRangePicker from "./MonthRangePicker";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: "16px 5px",
    backgroundColor: "#fff",
    borderRadius: 0,
    boxShadow: "none",
    borderBottom: "1px solid #e0e0e0",
  },
  prompt: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
  },
  row: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-end",
    gap: theme.spacing(3),
  },
  filter: {
    minWidth: 140,
    maxWidth: 160,
    flex: "0 1 auto",
  },
  disabledOverlay: {
    opacity: 0.55,
    pointerEvents: "none",
  },
  pillBtn: {
    backgroundColor: "#ec6535",
    color: "#fff",
    textTransform: "none",
    borderRadius: 9999,
    padding: "6px 22px",
    boxShadow: "none",
    "&:hover": { backgroundColor: "#9E4D27", boxShadow: "none" },
    "&.Mui-disabled": { backgroundColor: "#e0d2cb", color: "#fff" },
  },
  clearLink: {
    color: "#1976d2",
    cursor: "pointer",
    fontSize: "0.875rem",
    textDecoration: "underline",
    marginLeft: theme.spacing(1),
    "&:hover": { color: "#0d47a1" },
  },
  utilityRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(1),
    borderTop: "1px dashed #eee",
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  syncBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  lastSyncedText: { color: "#666", fontSize: "0.85rem" },
  refreshLink: {
    color: "#ec6535",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    fontSize: "0.85rem",
    fontStyle: "italic",
    "&:hover": { color: "#9E4D27" },
  },
  perPageBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginLeft: "auto",
  },
  perPageLabel: {
    fontSize: "0.8rem",
    color: "#666",
    letterSpacing: "0.05em",
  },
  perPageSelect: { minWidth: 70 },
}));

export default function FilterBar({
  tokenData,
  filters,
  sponsor,
  editMode,
  filtersSaveLoading,
  updateFilters,
  updateFiltersRaw,
  clearFilters,
  onSaveFilters,
  onViewReports,
  showRefresh,
  lastSyncTime,
  dataLoading,
  onRefresh,
  onPerPageChange,
}) {
  const classes = useStyles();
  const orgFilterValid = sponsor ? !!filters.cmoOrgID : !!filters.sponsorOrgID;
  const canSearch = orgFilterValid && !!filters.brand && !editMode;

  return (
    <Paper className={classes.paper}>
      <Typography className={classes.prompt}>
        Please enter any of the following values to view the report:
      </Typography>
      <Box className={`${classes.row} ${editMode ? classes.disabledOverlay : ""}`}>
        <Box className={classes.filter}>
          {sponsor ? (
            <CMOSelect
              value={filters.cmoOrgID}
              onChange={(id, label) =>
                updateFilters({ cmoOrgID: id, cmoLabel: label })
              }
            />
          ) : (
            <SponsorSelect
              tokenData={tokenData}
              value={filters.sponsorOrgID}
              onChange={(id, label) =>
                updateFilters({ sponsorOrgID: id, sponsorLabel: label })
              }
            />
          )}
        </Box>
        <Box className={classes.filter}>
          <BrandSelect
            value={filters.brand}
            onChange={(brand) => updateFilters({ brand })}
          />
        </Box>
        <Box className={classes.filter} style={{ minWidth: 200 }}>
          {/* <MFGPlantMultiSelect
            value={filters.aslNumberList}
            onChange={(aslNumberList) => updateFilters({ aslNumberList })}
          /> */}
          <MFGPlantSelect
            value={filters.aslNumber}
            onChange={(aslNumber) => updateFilters({ aslNumber })}
          />
        </Box>
        <Box>
          <Typography variant="caption">Select Month Range</Typography>
          <MonthRangePicker
            startMonth={filters.startMonth}
            endMonth={filters.endMonth}
            onChange={(patch) => updateFiltersRaw(patch)}
          />
        </Box>
        <Button
          variant="contained"
          className={classes.pillBtn}
          disabled={!canSearch}
          onClick={onViewReports}
        >
          View Forecast
        </Button>
        <Button
          variant="contained"
          className={classes.pillBtn}
          onClick={onSaveFilters}
          disabled={filtersSaveLoading || !canSearch}
          startIcon={
            filtersSaveLoading ? (
              <CircularProgress size={14} style={{ color: "#fff" }} />
            ) : null
          }
        >
          Save Filters
        </Button>
        <Typography className={classes.clearLink} onClick={clearFilters}>
          Clear All Filters
        </Typography>
      </Box>

      {showRefresh && (
        <Box className={classes.utilityRow}>
          <Box style={{ display: 'flex', flexDirection: 'column' }}>
            <Box className={classes.syncBox}>
              <Typography variant="body2" className={classes.lastSyncedText}>
                {`Last Synced @ ${moment(lastSyncTime).format(
                  "HH:mm:ss; DD MMMM, YYYY"
                )}`}
              </Typography>
              <Typography
                variant="body2"
                className={classes.refreshLink}
                onClick={() =>
                  !dataLoading && !editMode && onRefresh && onRefresh()
                }
              >
                Refresh
                <RefreshIcon fontSize="small" />
              </Typography>
            </Box>
            <Box className={classes.perPageBox}>
              <Typography className={classes.perPageLabel}>PER PAGE</Typography>
              <FormControl size="small">
                <Select
                  value={filters.pageSize}
                  onChange={onPerPageChange}
                  disabled={editMode}
                  variant="outlined"
                  className={classes.perPageSelect}
                >
                  {[10, 20, 30, 50].map((n) => (
                    <MenuItem key={n} value={n}>
                      {n}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Box>
      )}
    </Paper>
  );
}
